/**
 * AsignacionEntranteBanner — alerta global estilo "llamada entrante" (call
 * center) para el médico, montada UNA sola vez en App.tsx (fuera del árbol de
 * rutas) para que NO se desmonte al navegar entre páginas — así el timbre y
 * el listener persisten sin importar en qué página/pestaña esté el médico.
 *
 * Cubre los DOS modos de asignación automática (functions/src/consultas/
 * gestionColas.ts, autoAsignarInterno):
 *   - Híbrido con oferta: consulta en estado 'asignada' → tarjeta con
 *     Aceptar/Rechazar (antes solo vivía dentro de SalasPage/ColaPacientesPage
 *     y desaparecía si el médico navegaba a otra página).
 *   - Automático directo: `medicos/{uid}.consultaActiva` poblado sin pasar
 *     por 'asignada' → banner con "Ir a la consulta" (antes silencioso, sin
 *     sonido ni alerta si el médico no estaba mirando la pantalla).
 *
 * Ambos casos ahora suenan (useRingtone) para que el médico se entere sin
 * importar la pestaña/página en la que esté.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { ArrowRight, PhoneIncoming } from 'lucide-react';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useRingtone } from '../hooks/useRingtone';
import {
  aceptarAsignacion as aceptarAsignacionSvc,
  rechazarAsignacion as rechazarAsignacionSvc,
} from '../services/consultaService';
import { consumirAceptacionPropia } from '../services/aceptacionPropia';
import { logClient } from '../services/obsClientLogger';

/** Telemetría de diagnóstico: registra en obs_events CADA arranque del timbre
 *  con su causa, la página y la versión del bundle — para poder rastrear
 *  reportes de "suena al tomar consulta" hasta el disparador exacto y saber
 *  si el navegador que sonó corría un bundle viejo en caché. */
function logRing(causa: 'oferta' | 'consulta_activa', consultaId: string, pathname: string): void {
  logClient({
    nivel: 'INFO',
    categoria: 'UI',
    codigo: 'RING_ASIGNACION',
    componente: 'AsignacionEntranteBanner',
    consultaId,
    mensaje: `Timbre de asignación (${causa})`,
    detalle: { causa, pathname, build: __BUILD_TS__ },
  });
}
import { PacienteAsignadoCard, type PrioridadConsulta as CardPrioridad } from './sala';
import type { Consulta, PrioridadConsulta } from '../types';
import styles from './AsignacionEntranteBanner.module.css';

const ASSIGNMENT_COUNTDOWN_SEC = 20;

function toCardPrioridad(p?: PrioridadConsulta): CardPrioridad {
  if (p === 'alta') return 'urgente';
  if (p === 'media') return 'alta';
  return 'normal';
}

function enPaginaDeConsulta(pathname: string, consultaId: string): boolean {
  return pathname.includes(`/consulta/${consultaId}/`);
}

export function AsignacionEntranteBanner() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const ringtone = useRingtone();

  const [oferta, setOferta] = useState<Consulta | null>(null);
  const [consultaActiva, setConsultaActiva] = useState<string | null>(null);
  const [procesando, setProcesando] = useState(false);

  // Oferta híbrida pendiente de aceptar/rechazar (estado 'asignada').
  useEffect(() => {
    // Bug real encontrado: este componente vive montado fuera de las rutas y
    // SOBREVIVE al cambio de sesión. Si el guard retornaba sin limpiar, la
    // oferta/consulta del médico anterior quedaba en memoria y el banner se
    // mostraba al siguiente usuario (admin, supervisor u OTRO médico) con una
    // consulta que no le pertenece.
    setOferta(null); // también cubre médico→OTRO médico (antes del 1er snapshot)
    if (!user?.uid || role !== 'medico') return;
    const q = query(
      collection(db, 'consultas'),
      where('estado', '==', 'asignada'),
      where('medicoId', '==', user.uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setOferta(null);
          return;
        }
        const docs = snap.docs
          .map((d) => ({ id: d.id, ...d.data() } as Consulta))
          .sort((a, b) => {
            const ta = (a.creadoEn as unknown as { toMillis?: () => number })?.toMillis?.() ?? 0;
            const tb = (b.creadoEn as unknown as { toMillis?: () => number })?.toMillis?.() ?? 0;
            return ta - tb;
          });
        setOferta(docs[0]);
      },
      (err) => console.warn('[AsignacionEntranteBanner] oferta subscription error:', err.message),
    );
    return () => unsub();
  }, [user?.uid, role]);

  // Consulta activa (asignación automática directa, o ya aceptada).
  useEffect(() => {
    // Mismo caso que arriba: limpiar al cambiar/cerrar sesión para que el
    // banner nunca herede la consulta activa del usuario anterior.
    setConsultaActiva(null); // ídem: nunca heredar la del usuario anterior
    if (!user?.uid || role !== 'medico') return;
    const unsub = onSnapshot(
      doc(db, 'medicos', user.uid),
      (snap) => {
        const ca = snap.data()?.consultaActiva as string | null | undefined;
        setConsultaActiva(typeof ca === 'string' && ca.length > 0 ? ca : null);
      },
      (err) => console.warn('[AsignacionEntranteBanner] consultaActiva subscription error:', err.message),
    );
    return () => unsub();
  }, [user?.uid, role]);

  // ── Timbre: suena al detectar una alerta NUEVA, no en cada render ──────
  const lastRungOfertaId = useRef<string | null>(null);
  const lastRungConsultaActivaId = useRef<string | null>(null);

  useEffect(() => {
    if (!oferta) {
      lastRungOfertaId.current = null;
      return;
    }
    if (oferta.id !== lastRungOfertaId.current) {
      lastRungOfertaId.current = oferta.id;
      // Bug real encontrado: tomar/aceptar una oferta desde la PROPIA cuenta
      // (p. ej. el médico aceptó una consulta directamente sin pasar por este
      // banner) escribe el mismo estado que una asignación sorpresa — el
      // timbre sonaba aunque el médico ya sabía y había iniciado la acción.
      if (!consumirAceptacionPropia(oferta.id)) {
        logRing('oferta', oferta.id, location.pathname);
        ringtone.start();
      }
    }
  }, [oferta, ringtone, location.pathname]);

  useEffect(() => {
    // Si hay una oferta pendiente, ese timbre manda — evita que ambos casos
    // suenen a la vez (no deberían coexistir: el backend no ofrece un nuevo
    // paciente a un médico ya en consulta).
    if (oferta) return;
    if (!consultaActiva) {
      lastRungConsultaActivaId.current = null;
      return;
    }
    if (consultaActiva !== lastRungConsultaActivaId.current) {
      lastRungConsultaActivaId.current = consultaActiva;
      // Mismo caso que arriba: "Tomar consulta"/"Atender" desde /salas o
      // /cola escribe `consultaActiva` directamente (asignación automática,
      // sin pasar por 'asignada') — acción propia, no debe sonar como
      // asignación sorpresa.
      if (!consumirAceptacionPropia(consultaActiva)) {
        logRing('consulta_activa', consultaActiva, location.pathname);
        ringtone.start();
      }
    }
  }, [consultaActiva, oferta, ringtone, location.pathname]);

  // Detener el timbre en cuanto la alerta deja de aplicar: se resolvió
  // (aceptada/rechazada/expirada en otra pestaña) o el médico ya está en la
  // página de esa videollamada (llegó ahí por otro camino, p.ej. la campana).
  // Además se AVISA a las demás pestañas (cada una tiene su propio banner y
  // su propio audio): sin esto, llegar a la consulta en una pestaña dejaba a
  // las otras repicando los ~20s completos.
  const activeIdRef = useRef<string | null>(null);
  const stopCanalRef = useRef<BroadcastChannel | null>(null);
  useEffect(() => {
    activeIdRef.current = oferta?.id ?? consultaActiva ?? null;
  }, [oferta, consultaActiva]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel('minsa-asignacion-ring');
    bc.onmessage = (ev: MessageEvent) => {
      const stopFor = (ev.data as { stopFor?: string } | null)?.stopFor;
      if (stopFor && stopFor === activeIdRef.current) ringtone.stop();
    };
    stopCanalRef.current = bc;
    return () => { bc.close(); stopCanalRef.current = null; };
  }, [ringtone]);

  useEffect(() => {
    const activeId = oferta?.id ?? consultaActiva ?? null;
    if (!activeId || enPaginaDeConsulta(location.pathname, activeId)) {
      ringtone.stop();
      if (activeId) stopCanalRef.current?.postMessage({ stopFor: activeId });
    }
  }, [oferta, consultaActiva, location.pathname, ringtone]);

  // El timbre ya cumplió su propósito si el médico está USANDO la app: al
  // primer gesto (clic o tecla) en esta pestaña se corta el sonido — el
  // banner sigue visible hasta que el médico actúe. Evita el repique de
  // ~20s "en la cara" del médico que ya está mirando la pantalla (reportado
  // como "el contador emite un sonido cada segundo": era este timbre).
  useEffect(() => {
    const activeId = oferta?.id ?? consultaActiva ?? null;
    if (!activeId) return;
    const onGesto = () => ringtone.stop();
    window.addEventListener('pointerdown', onGesto, true);
    window.addEventListener('keydown', onGesto, true);
    return () => {
      window.removeEventListener('pointerdown', onGesto, true);
      window.removeEventListener('keydown', onGesto, true);
    };
  }, [oferta, consultaActiva, ringtone]);

  const handleAceptar = async () => {
    if (!oferta) return;
    setProcesando(true);
    ringtone.stop();
    try {
      await aceptarAsignacionSvc(oferta.id);
      addToast('success', `Atendiendo a ${oferta.pacienteNombre}`);
      navigate(`/consulta/${oferta.id}/video-ai`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo aceptar la asignación';
      addToast('error', msg);
    } finally {
      setProcesando(false);
    }
  };

  const handleRechazar = async () => {
    if (!oferta) return;
    setProcesando(true);
    try {
      await rechazarAsignacionSvc(oferta.id);
      addToast('info', 'Asignación rechazada, paciente devuelto a la cola');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo rechazar la asignación';
      addToast('error', msg);
    } finally {
      setProcesando(false);
    }
  };

  // Cinturón de seguridad: las alertas de asignación son EXCLUSIVAS del rol
  // médico — nunca renderizar para admin/supervisor/sesión cerrada, aunque
  // quede estado en memoria por una carrera al cambiar de sesión.
  if (!user?.uid || role !== 'medico') return null;

  // No mostrar nada si el médico ya está en la página de esa videollamada —
  // ya está atendiendo, no hace falta interrumpirlo con su propia alerta.
  if (oferta && !enPaginaDeConsulta(location.pathname, oferta.id)) {
    return (
      <div className={styles.overlay} role="presentation">
        <div className={styles.cardWrap}>
          <div className={styles.pulseGlow} aria-hidden="true" />
          <PacienteAsignadoCard
            pacienteNombre={oferta.pacienteNombre}
            pacienteEdad={oferta.pacienteEdad ?? null}
            pacienteSexo={oferta.pacienteSexo ?? null}
            pacienteFotoURL={oferta.pacienteFotoURL}
            motivoConsulta={oferta.motivoConsulta}
            prioridad={toCardPrioridad(oferta.prioridad)}
            esperaMin={
              oferta.creadoEn
                ? Math.max(
                    0,
                    Math.floor(
                      (Date.now() -
                        (oferta.creadoEn as unknown as { toMillis: () => number }).toMillis()) /
                        60000,
                    ),
                  )
                : undefined
            }
            countdownSec={ASSIGNMENT_COUNTDOWN_SEC}
            onAccept={handleAceptar}
            onReject={handleRechazar}
            loading={procesando}
          />
        </div>
      </div>
    );
  }

  if (consultaActiva && !enPaginaDeConsulta(location.pathname, consultaActiva)) {
    return (
      <div className={styles.overlay} role="presentation">
        <div className={`${styles.banner} ${styles.bannerActiva}`} role="alert" aria-live="assertive">
          <span className={styles.ringIconWrap} aria-hidden="true">
            <PhoneIncoming size={18} className={styles.ringIcon} />
          </span>
          <div className={styles.body}>
            <strong>Paciente asignado</strong>
            <span>Tiene una videollamada esperando. Toque para atenderla.</span>
          </div>
          <button
            type="button"
            className={styles.btnGo}
            onClick={() => {
              ringtone.stop();
              navigate(`/consulta/${consultaActiva}/video-ai`);
            }}
          >
            Ir a la consulta <ArrowRight size={14} aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return null;
}
